import axios from "axios";

export const METHOD_GET = "GET",
  METHOD_POST = "POST",
  METHOD_PUT = "PUT",
  METHOD_DELETE = "DELETE";

/**
 * Join the API base with a relative path.
 *
 * These were previously concatenated directly, so REACT_APP_BASE_URL had to
 * end in "/" or every request in the app silently became a malformed URL
 * ("http://host:3000" + "user/login" -> "http://host:3000user/login"). A
 * missing character in an env file should not be able to take down the app.
 */
export function joinUrl(base, path) {
  const cleanBase = String(base || "").replace(/\/+$/, "");
  const cleanPath = String(path || "").replace(/^\/+/, "");
  if (!cleanBase) {
    // Fall back to a same-origin relative path rather than requesting
    // "undefinedputh/to/thing".
    return `/${cleanPath}`;
  }
  return `${cleanBase}/${cleanPath}`;
}

export default async function axiosFactory({
  url,
  method,
  data,
  isFake = false,
}) {
  // An absolute URL is used as-is; only relative paths get the API base.
  if (/^https?:\/\//i.test(url)) {
    isFake = true;
  }
  const header = getHeader(method, data, url);
  const urlToUse = isFake ? url : joinUrl(process.env.REACT_APP_BASE_URL, url);
  let response = { status: "", data: "", error: "" };
  try {
    switch (method) {
      case METHOD_GET:
        response = await axios.get(urlToUse, header);
        break;
      case METHOD_POST:
        response = await axios.post(urlToUse, data, header);
        break;
      case METHOD_PUT:
        response = await axios.put(urlToUse, data, header);
        break;
      case METHOD_DELETE:
        response = await axios.delete(urlToUse, header);
        break;
      default:
        new Error("Method not supported");
    }
  } catch (error) {
    if (!error.response) {
      response.status = 0;
      response.error = "Network error";
      response.data =
        error.message || "Unable to reach the server. Please try again.";
      return {
        data: response.data,
        status: response.status,
        error: response.error,
      };
    }

    switch (error.response.status) {
      case 401:
        response.status = 401;
        response.error = "Unauthorized";
        response.data = error.response.data.message;
        getAccessToken();
        break;
      case 403:
        response.status = 403;
        response.error = "Forbidden";
        response.data = error.response.data.message;
        getAccessToken();
        break;
      case 404:
        response.status = 404;
        response.error = "Not found";
        response.data = error.response.data.message;
        break;
      case 500:
        response.status = 500;
        response.error = "Internal server error";
        response.data = error.response.data.message;
        break;
      default:
        response.status = 400;
        response.error = "Something went wrong";
        response.data = error.response.data.message;
        break;
    }
  } finally {
    return {
      data: response.data,
      status: response.status,
      error: response.error,
    };
  }
}

function getHeader(method, data, url) {
  let header = {
    "Content-Type": "application/json",
    authorization: `Bearer ${localStorage.getItem("jwtAccessToken")}`,
  };
  if (url.includes("images")) {
    header = {
      "Content-Type": "application/json",
    };
  }
  if (method === METHOD_DELETE) {
    return { headers: header, data: data };
  }
  return { headers: header };
}

async function getAccessToken() {
  let header = {
    "Content-Type": "application/json",
    authorization: `Bearer ${localStorage.getItem("jwtRefreshToken")}`,
  };
  if (!localStorage.getItem("jwtRefreshToken")) {
    return;
  }
  let url = joinUrl(process.env.REACT_APP_BASE_URL, "user/refresh");
  try {
    let response = await axios.get(url, header);
    if (response.status === 200) {
      localStorage.setItem("jwtAccessToken", response.data.data.accessToken);
      return;
    }
  } catch (error) {
    localStorage.removeItem("user");
    localStorage.removeItem("jwtAccessToken");
    localStorage.removeItem("jwtRefreshToken");
    window.location.href = "/";
  }
}
